import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {Eyebrow} from 'components/DSYS/Eyebrow';
import {createContext, type ReactNode, useContext} from 'react';
import {FadeIn, useHasMounted} from 'views/workstreams/page/FadeIn';
import {SectionFoldButton} from 'views/workstreams/page/SectionFoldToggle';

import styles from './WorkstreamDetailsShell.module.css';

/**
 * 'label' renders main-section titles as quiet sentence-case labels instead of
 * uppercase eyebrows (opt-in; used by the /workstreams detail page).
 */
export const SectionCardTitleStyleContext = createContext<'eyebrow' | 'label'>('eyebrow');

export const SectionTitle = ({children}: {children: ReactNode}) =>
  useContext(SectionCardTitleStyleContext) === 'label' ? (
    <Text size="small" isBold color="subtle">
      {children}
    </Text>
  ) : (
    <Eyebrow size="small">{children}</Eyebrow>
  );

/**
 * A collapsible section's chevron + title. On 'label' pages (the /workstreams
 * detail) it's the cards' fold button (hover pill, fold analytics); elsewhere
 * the plain one.
 */
export const SectionToggleButton = ({
  fold,
  title,
  isExpanded,
  onToggle,
  ariaLabel,
}: {
  // Which section this is, for the fold analytics.
  fold: string;
  title: ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  ariaLabel?: string;
}) =>
  useContext(SectionCardTitleStyleContext) === 'label' ? (
    <SectionFoldButton
      fold={fold}
      label={title}
      open={isExpanded}
      onToggle={onToggle}
      ariaLabel={ariaLabel}
    />
  ) : (
    <button
      type="button"
      className={styles.activityToggle}
      aria-expanded={isExpanded}
      aria-label={ariaLabel}
      onClick={onToggle}
    >
      <UIIcon
        src={isExpanded ? ChevronDownLine : ChevronRightLine}
        size="small"
        className={styles.activityToggleIcon}
      />
      <SectionTitle>{title}</SectionTitle>
    </button>
  );

export const WorkstreamSectionCard = ({
  title,
  titleAction,
  subtitle,
  action,
  headerClassName,
  children,
  variant = 'main',
  collapsible = false,
  isExpanded = true,
  onExpandedChange,
  toggleAriaLabel,
  fold = 'section',
}: {
  title: ReactNode;
  titleAction?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  headerClassName?: string;
  children: ReactNode;
  variant?: 'main' | 'sidebar';
  collapsible?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  toggleAriaLabel?: string;
  // A collapsible section's name, for the fold analytics.
  fold?: string;
}) => {
  const showContent = !collapsible || isExpanded;
  const mounted = useHasMounted();
  // 'label' pages space sections themselves (even gaps), so drop the card's own
  // top padding / header offset.
  const flush = useContext(SectionCardTitleStyleContext) === 'label';

  if (variant === 'main') {
    // A falsy title with no actions renders the body alone (no header row).
    const hasHeader = Boolean(title || titleAction || action);
    return (
      <section className={styles.mainSection} style={flush ? {paddingTop: 0} : undefined}>
        {hasHeader ? (
          <div
            className={`${styles.activityHeader} ${headerClassName ?? ''}`.trim()}
            style={flush ? {marginTop: 0} : undefined}
          >
            {collapsible ? (
              <div className={styles.mainSectionTitleRow}>
                <SectionToggleButton
                  fold={fold}
                  title={title}
                  isExpanded={isExpanded}
                  onToggle={() => onExpandedChange?.(!isExpanded)}
                  ariaLabel={toggleAriaLabel}
                />
                {showContent ? titleAction : null}
              </div>
            ) : (
              <div className={styles.mainSectionTitleRow}>
                <SectionTitle>{title}</SectionTitle>
                {titleAction}
              </div>
            )}
            {action && showContent ? (
              <div className={styles.mainSectionAction}>{action}</div>
            ) : null}
          </div>
        ) : null}
        {subtitle && showContent ? (
          <Text size="small" className={styles.subtleText}>
            {subtitle}
          </Text>
        ) : null}
        {showContent ? (
          collapsible ? (
            // Fades in when opened after the page arrived.
            <FadeIn enabled={mounted}>
              <div className={styles.mainSectionBody}>{children}</div>
            </FadeIn>
          ) : (
            <div className={styles.mainSectionBody}>{children}</div>
          )
        ) : null}
      </section>
    );
  }

  return (
    <section className={`${styles.sectionCard} ${styles.sectionCardSidebar}`.trim()}>
      <div className={`${styles.sectionHeader} ${styles.sectionHeaderSidebar}`.trim()}>
        <Stack gap="4">
          <Eyebrow>{title}</Eyebrow>
          {subtitle ? (
            <Text size="small" className={styles.subtleText}>
              {subtitle}
            </Text>
          ) : null}
        </Stack>
        {action}
      </div>
      <div className={`${styles.sectionBody} ${styles.sectionBodySidebar}`.trim()}>{children}</div>
    </section>
  );
};
